import { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { axiosClient } from "../configs/axiosClient";

type UploadKind = "image" | "video";

interface UseFileUploadOptions {
  endpoint: string;
  kind: UploadKind;
  maxFiles?: number;
  maxFileSize?: number;
  fieldName?: string;
}

interface UploadResult<T> {
  data: T | null;
  ok: boolean;
}

const DEFAULT_LIMITS = {
  image: 5 * 1024 * 1024,
  video: 95 * 1024 * 1024, // dưới ngưỡng nginx 100MB để chừa multipart overhead
};

export const useFileUpload = <T = unknown>({
  endpoint,
  kind,
  maxFiles = 1,
  maxFileSize,
  fieldName = "file",
}: UseFileUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false);

  const limit = maxFileSize ?? DEFAULT_LIMITS[kind];

  const formatMB = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

  const validate = (files: File[]): string | null => {
    if (files.length === 0) return "Chưa chọn file";
    if (files.length > maxFiles) {
      return `Chỉ được chọn tối đa ${maxFiles} file/lần. Bạn đã chọn ${files.length}.`;
    }
    for (const file of files) {
      if (kind === "image" && !file.type.startsWith("image/")) {
        return `File "${file.name}" không phải ảnh`;
      }
      if (kind === "video" && !file.type.startsWith("video/")) {
        return `File "${file.name}" không phải video`;
      }
      if (file.size > limit) {
        return `File "${file.name}" quá lớn (${formatMB(file.size)}MB). Tối đa ${formatMB(limit)}MB.`;
      }
    }
    return null;
  };

  const upload = async (
    input: FileList | File[] | File | null,
  ): Promise<UploadResult<T>> => {
    if (!input) return { data: null, ok: false };

    const files: File[] =
      input instanceof File
        ? [input]
        : Array.from(input as FileList | File[]);

    const error = validate(files);
    if (error) {
      toast.error(error);
      return { data: null, ok: false };
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append(fieldName, f));
      const response = await axiosClient.post<T>(endpoint, formData);
      return { data: response.data, ok: true };
    } catch (err) {
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? "Upload thất bại"
          : "Upload thất bại";
      toast.error(message);
      return { data: null, ok: false };
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading };
};
